export interface FieldDef {
  key: string;
  label: string;
  /** Shown behind an info icon beside the switch, when the label needs saying. */
  note?: string;
}

export interface SheetDef {
  tab: string;
  sheetKey: string;
  label: string;
  coverageGroup: string;
  /** Shown behind an info icon beside the heading, when the scope needs saying. */
  note?: string;
  fields: FieldDef[];
}

function toLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bFica\b/, "FICA")
    .replace(/\bW 2\b/, "W-2")
    .replace(/\bPfl\b/, "PFL")
    .replace(/\bEap\b/, "EAP")
    .replace(/\bCola\b/, "COLA")
    .replace(/\bAls\b/, "ALS")
    .replace(/\bAd D\b/, "AD&D")
    .replace(/\bMri\b/, "MRI")
    .replace(/\bTmj\b/, "TMJ")
    .replace(/\bAso\b/, "ASO")
    .replace(/\bDi\b/, "DI");
}

function fields(keys: string[]): FieldDef[] {
  return keys.map((key) => ({
    key,
    label: HIDE_ROW_LABELS[key] ?? toLabel(key),
  }));
}

// The renewal hide-row controls are prefixed per coverage sheet (two exist on
// each: a percentage row and a dollar row), so they are labelled explicitly
// rather than derived from the key.
//
// The workbook names these rows for what they suppress ("No Renewal ($) - Hide
// Row"); the labels here name what the row shows, which is what an admin is
// choosing between.
const renewalHideRows = (sheetKey: string): FieldDef[] => [
  {
    key: `${sheetKey}_no_renewal_percent_hide_row`,
    label: "Variance to Renewal (%)",
  },
  {
    key: `${sheetKey}_no_renewal_dollar_hide_row`,
    label: "Variance to Renewal ($)",
  },
];

// Same relabelling for the two unprefixed hide-row controls, which `fields()`
// would otherwise derive straight from the key.
const HIDE_ROW_LABELS: Record<string, string> = {
  no_current_hide_row: "Variance to Current ($)",
  no_revised_renewal_hide_row: "Variance to Revised Renewal ($)",
  // toLabel() would otherwise render this as "24 Hour Coverage" — no hyphen,
  // capital H — which doesn't match the grid/MP row's "24-hour Coverage".
  "24_hour_coverage": "24-hour Coverage",
  // toLabel() would render this as "Newborn Nursery Care" — no slash — which
  // doesn't match the grid/MP row's "Newborn/ Nursery Care".
  newborn_nursery_care: "Newborn/ Nursery Care",
};

export const fieldCatalog: SheetDef[] = [
  {
    tab: "quotes",
    sheetKey: "client_profile",
    label: "Client Profile",
    coverageGroup: "Client",
    fields: fields(["consultant_name", "account_manager_rfp_contact"]),
  },
  {
    tab: "quotes",
    sheetKey: "financial_summary_page",
    label: "Financial Summary",
    coverageGroup: "Financial",
    fields: fields(["hide_tech_credits"]),
  },
  {
    tab: "quotes",
    sheetKey: "dental",
    label: "Dental",
    coverageGroup: "Dental",
    note:
      "These settings apply to every dental sheet the marketing presentation " +
      "renders — PPO, DHMO, Dual Option and the ASO variants — turning each " +
      "field on or off wherever that sheet has a row for it.",
    fields: [
      ...fields([
        "eligibility_class",
        "plan_name",
        "network",
        "preventive_advantage",
        "oral_exams",
        "oral_exam_frequency",
        "cleaning_frequency",
        "bitewing_x_rays",
        "full_mouth_x_rays",
        "full_mouth_x_rays_frequency",
        "sealants",
        "fluoride_treatments",
        "space_maintainers",
        "general_anesthesia",
        "composite_white_fillings",
        "amalgam_silver_fillings",
        "simple_extractions",
        "oral_surgery_simple",
        "oral_surgery_complex",
        "bridges_dentures",
        "bridges_dentures_frequency",
        "crowns",
        "crowns_frequency",
        "implants",
        "tmj",
        "missing_tooth_exclusion",
        "work_in_progress_included",
        "open_or_closed_contract",
        "eligibility_hours",
        "eligibility_status",
        "annual_open_enrollment",
        "dependent_age_limit",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("dental"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "dental_multiple_options",
    label: "Dental Multiple Options",
    coverageGroup: "Dental",
    note:
      "The DHMO/PPO combination sheets — Dual Option and Tri Option — share " +
      "these settings, kept apart from Dental because some brokers hide rows " +
      "here that they show on the other dental sheets.",
    fields: [
      ...fields([
        "eligibility_class",
        "plan_name",
        "network",
        "oral_exams",
        "oral_exam_frequency",
        "cleaning_frequency",
        "full_mouth_x_rays",
        "full_mouth_x_rays_frequency",
        "sealants",
        "space_maintainers",
        "general_anesthesia",
        "composite_white_fillings",
        "amalgam_silver_fillings",
        "simple_extractions",
        "missing_tooth_exclusion",
        "eligibility_hours",
        "annual_open_enrollment",
        "dependent_age_limit",
        "employer_contribution",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("dental_multiple_options"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "vision",
    label: "Vision",
    coverageGroup: "Vision",
    note:
      "These settings apply to every vision sheet the marketing presentation " +
      "renders — single and dual option, and the ASO variants — turning each " +
      "field on or off wherever that sheet has a row for it.",
    fields: [
      ...fields([
        "eligibility_class",
        "plan_name",
        "lenticular_lenses_after_copay_allowance",
        "standard_progressive",
        "premium_progressive",
        "conventional",
        "disposable",
        "contacts_elective",
        "contacts_med_nec",
        "contact_lens_exam",
        "progressive_lenses",
        "annual_open_enrollment",
        "eligibility_hours",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("vision"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "std",
    label: "STD",
    coverageGroup: "Disability",
    fields: [
      ...fields([
        "eligibility_hours",
        "minimum_benefit",
        "first_day_hospital",
        "definition_of_disability",
        "partial_disability_formula",
        "partial_disability",
        "earnings_test",
        "pre_existing_condition",
        "portability",
        "rehabilitative_incentive_benefit",
        "definition_of_earnings",
        "recurrent_disability",
        "fica_match",
        "billable_or_non_billable",
        "w_2_prep",
        "claims_intake",
        "statutory_di_integration",
        "pfl_integration",
        "enrolled_lives",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("std"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "vol_std",
    label: "Vol STD",
    coverageGroup: "Disability",
    fields: [
      ...fields([
        "eligibility_hours",
        "minimum_benefit",
        "first_day_hospital",
        "definition_of_disability",
        "partial_disability_formula",
        "partial_disability",
        "earnings_test",
        "pre_existing_condition",
        "portability",
        "rehabilitative_incentive_benefit",
        "definition_of_earnings",
        "recurrent_disability",
        "fica_match",
        "billable_or_non_billable",
        "w_2_prep",
        "claims_intake",
        "annual_open_enrollment",
        "statutory_di_integration",
        "pfl_integration",
        "enrolled_lives",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("vol_std"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "aso_std",
    label: "ASO STD",
    coverageGroup: "Disability",
    fields: [
      ...fields([
        "type",
        "eligibility_hours",
        "minimum_benefit",
        "first_day_hospital",
        "definition_of_disability",
        "partial_disability_formula",
        "partial_disability",
        "earnings_test",
        "pre_existing_condition",
        "portability",
        "rehabilitative_incentive_benefit",
        "definition_of_earnings",
        "recurrent_disability",
        "fica_match",
        "billable_or_non_billable",
        "w_2_prep",
        "claims_intake",
        "statutory_di_integration",
        "pfl_integration",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("aso_std"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "ltd",
    label: "LTD",
    coverageGroup: "Disability",
    fields: [
      ...fields([
        "eligibility_hours",
        "minimum_amount",
        "guarantee_issue",
        "gainful_earnings_test_own_occupation",
        "gainful_earnings_test_any_occupation",
        "zero_day_residual",
        "indexing_of_pre_disability_earnings",
        "return_to_work_incentive_period",
        "definition_of_earnings",
        "definition_of_partial_disability",
        "other_income_integration",
        "social_security_integration",
        "trial_work_days",
        "survivor_benefit",
        "worksite_modification",
        "day_care_benefits",
        "reasonable_accommodation",
        "rehabilitative_incentive_benefit",
        "prudent_person",
        "self_reported_limitations",
        "special_conditions_limitations",
        "mental_illness",
        "substance_abuse",
        "recovery_benefit",
        "mandatory_rehabilitation",
        "residency_restriction",
        "progressive_illness",
        "infectious_contagious_disease",
        "specific_loss_ad_d_and_loss_of_sight",
        "activities_of_daily_living",
        "maximum_capacity",
        "rehab_mandatory_or_enhanced",
        "retirement_income_protection",
        "recurrent_disability",
        "cola_included",
        "conversion",
        "waiver_of_premium",
        "portability",
        "eap",
        "enrolled_lives",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("ltd"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "vol_ltd",
    label: "Vol LTD",
    coverageGroup: "Disability",
    fields: [
      ...fields([
        "eligibility_hours",
        "minimum_amount",
        "guarantee_issue",
        "gainful_earnings_test_own_occupation",
        "gainful_earnings_test_any_occupation",
        "definition_of_partial_disability",
        "indexing_of_pre_disability_earnings",
        "return_to_work_incentive_period",
        "definition_of_earnings",
        "zero_day_residual",
        "other_income_integration",
        "social_security_integration",
        "trial_work_days",
        "survivor_benefit",
        "worksite_modification",
        "day_care_benefits",
        "reasonable_accommodation",
        "rehabilitative_incentive_benefit",
        "prudent_person",
        "self_reported_limitations",
        "special_conditions_limitations",
        "mental_illness",
        "substance_abuse",
        "recovery_benefit",
        "mandatory_rehabilitation",
        "residency_restriction",
        "progressive_illness",
        "infectious_contagious_disease",
        "specific_loss_ad_d_and_loss_of_sight",
        "activities_of_daily_living",
        "maximum_capacity",
        "rehab_mandatory_or_enhanced",
        "retirement_income_protection",
        "recurrent_disability",
        "cola_included",
        "conversion",
        "waiver_of_premium",
        "portability",
        "eap",
        "annual_open_enrollment",
        "enrolled_lives",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("vol_ltd"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "basic_life",
    label: "Basic Life",
    coverageGroup: "Life",
    fields: [
      ...fields([
        "eligibility_hours",
        "maximum_amount",
        "definition_of_earnings",
        "conversion",
        "accelerated_death_benefit",
        "waiver_of_premium",
        "waiver_of_premium_eligibility",
        "waiver_of_premium_waiting_period",
        "waiver_of_premium_duration",
        "air_bag",
        "common_carrier",
        "repatriation",
        "seat_belt",
        "eap_number_of_face_to_face_visits",
        "travel_aid",
        "enrolled_lives",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("basic_life"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "basic_life_add_age_banded",
    label: "Basic Life AD&D Age Banded",
    coverageGroup: "Life",
    fields: [
      ...fields([
        "eligibility_hours",
        "maximum_amount",
        "definition_of_earnings",
        "conversion",
        "accelerated_death_benefit",
        "waiver_of_premium",
        "waiver_of_premium_eligibility",
        "waiver_of_premium_waiting_period",
        "waiver_of_premium_duration",
        "air_bag",
        "common_carrier",
        "repatriation",
        "seat_belt",
        "eap_number_of_face_to_face_visits",
        "travel_aid",
        "enrolled_lives",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("basic_life_add_age_banded"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "basic_life_add_dep",
    label: "Basic Life AD&D & Dep",
    coverageGroup: "Life",
    fields: [
      ...fields([
        "eligibility_hours",
        "maximum_amount",
        "definition_of_earnings",
        "conversion",
        "accelerated_death_benefit",
        "waiver_of_premium",
        "waiver_of_premium_eligibility",
        "waiver_of_premium_waiting_period",
        "waiver_of_premium_duration",
        "air_bag",
        "common_carrier",
        "repatriation",
        "seat_belt",
        "eap_number_of_face_to_face_visits",
        "travel_aid",
        "enrolled_lives",
        "no_current_hide_row",
        "no_revised_renewal_hide_row",
      ]),
      ...renewalHideRows("basic_life_add_dep"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "vol_life",
    label: "Vol Life",
    coverageGroup: "Life",
    fields: [
      ...fields([
        "eligibility_hours",
        "age_reductions_ee",
        "age_reductions_sp",
        "definition_of_earnings",
        "age_reductions",
        "portability",
        "conversion",
        "accelerated_death_benefit",
        "suicide_exclusion",
        "waiver_of_premium",
        "waiver_of_premium_eligibility",
        "waiver_of_premium_waiting_period",
        "waiver_of_premium_duration",
        "air_bag",
        "common_carrier",
        "repatriation",
        "seat_belt",
        "annual_open_enrollment",
        "annual_ability_to_increase_benefit",
        "spouse_age_basis_ee_or_sp_age",
        "enrolled_lives",
        "no_current_hide_row",
      ]),
      ...renewalHideRows("vol_life"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "accident",
    label: "Accident",
    coverageGroup: "Supplemental",
    fields: [
      ...fields([
        "accidental_death_dismemberment",
        "laceration_maximum_benefit",
        "coma",
        "major_diagnostic_exam_x_ray",
        "major_diagnostic_exam_mri",
        "eligibility_hours",
        "annual_open_enrollment",
        "volume_illustrated_from",
        "enrolled_lives",
      ]),
      {
        key: "coma_included_not_included",
        label: "Coma as Included / Not Included",
        note:
          "On, the Coma row prints “Included” wherever a dollar amount was " +
          "quoted and “Not Included” wherever no Coma benefit was. Off, it " +
          "prints the quoted amount — “Up to $10,000”.",
      },
    ],
  },
  {
    tab: "quotes",
    sheetKey: "eap",
    label: "EAP",
    coverageGroup: "Supplemental",
    fields: fields(["additional_resources_included"]),
  },
  {
    tab: "quotes",
    sheetKey: "critical_illness",
    label: "Critical Illness",
    coverageGroup: "Supplemental",
    fields: [
      ...fields([
        "skin_cancer",
        "advanced_parkinsons",
        "als",
        "eligibility_hours",
        "annual_open_enrollment",
        "volume_illustrated_from",
        "enrolled_lives",
      ]),
      {
        key: "display_full_age_bands",
        label: "Display Full Age Bands",
        note:
          "On, the rate table prints the full age band ranges — “<25”, " +
          "“25 - 29” through “80+”. Off, it prints the sample ages alone — " +
          "“25”, “35”, “45”, “55”, “65”.",
      },
    ],
  },
  {
    tab: "quotes",
    sheetKey: "cancer",
    label: "Cancer",
    coverageGroup: "Supplemental",
    fields: fields([
      "eligibility_hours",
      "annual_open_enrollment",
      "volume_illustrated_from",
      "enrolled_lives",
    ]),
  },
  {
    tab: "quotes",
    sheetKey: "hospital_indemnity",
    label: "Hospital Indemnity",
    coverageGroup: "Supplemental",
    fields: [
      ...fields([
        "eligibility",
        "24_hour_coverage",
        "pregnancy",
        "newborn_nursery_care",
        "eligibility_hours",
        "annual_open_enrollment",
        "volume_illustrated_from",
        "enrolled_lives",
      ]),
      // A footnote row rather than a benefit: keyed short, labelled with the
      // sentence the workbook prints so admins recognise what they are toggling.
      {
        key: "pregnancy_waiting_period_note",
        label:
          "Pregnancy benefits may be subject to a waiting period. Refer to the carrier proposals for details.",
      },
    ],
  },
  {
    tab: "quotes",
    sheetKey: "whole_life",
    label: "Whole Life",
    coverageGroup: "Life",
    fields: fields(["annual_open_enrollment"]),
  },
  {
    tab: "quotes",
    sheetKey: "fmla",
    label: "FMLA",
    coverageGroup: "Other",
    fields: [
      ...fields(["no_current_hide_row", "no_revised_renewal_hide_row"]),
      ...renewalHideRows("fmla"),
    ],
  },
  {
    tab: "quotes",
    sheetKey: "idi",
    label: "IDI",
    coverageGroup: "Other",
    fields: fields(["enrolled_lives"]),
  },
];
